/**
 * One-tap "Start together".
 *
 * Collapses the five gates between "I'm looking at a room" and "my
 * partner can edit it with me" into a single orchestration:
 *
 *   sign in → save the room → promote it to a shared room →
 *   mint a link invite → enter the live session → hand back the link.
 *
 * Each step leaves the app in a state the user could have reached by
 * hand (saved-but-private, shared-but-no-invite, …), so a failure
 * midway is recoverable from the normal UI. The step that failed is
 * reported through `start_together_failed` and the thrown
 * {@link StartTogetherError} so the checklist can say what to try next.
 */

import { getCurrentUser, signInWithGoogle, AuthError, waitForAuthReady } from "./auth";
import { useDesign } from "./store";
import { useLibrary } from "./library";
import { openRoom } from "./openRoom";
import {
  buildInviteLink,
  callCreateShareInvite,
  callCreateSharedRoom,
} from "./sharedInvites";
import { fetchSharedRoom } from "./sharedRoomsCloud";
import { isInAppBrowser } from "./inAppBrowser";
import { addBreadcrumb, captureException, track } from "./telemetry";
import { recordInviteLink } from "./collabOnboarding";

export type StartTogetherStep = "sign_in" | "save" | "promote" | "invite" | "open";

export class StartTogetherError extends Error {
  readonly step: StartTogetherStep;
  readonly cause?: unknown;
  constructor(step: StartTogetherStep, message: string, cause?: unknown) {
    super(message);
    this.name = "StartTogetherError";
    this.step = step;
    this.cause = cause;
  }
}

/** Thrown when sign-in is completing via a full-page redirect. The page
 *  is about to navigate; callers should keep their busy state. */
export class StartTogetherRedirecting extends Error {
  constructor() {
    super("Redirecting to Google sign-in…");
    this.name = "StartTogetherRedirecting";
  }
}

export interface StartTogetherResult {
  link: string;
  roomId: string;
  roomName: string;
}

const PENDING_KEY = "room-canvas:onboarding:start-together-pending";

/** Whether a redirect sign-in was started by "Start together" and the
 *  orchestration should resume once auth restores. */
export function isStartTogetherPending(): boolean {
  try {
    return window.localStorage.getItem(PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

function setPending(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(PENDING_KEY, "1");
    else window.localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Run the orchestration. `onStep` is called as each step begins so the UI
 * can narrate progress ("Signing in…", "Saving…").
 */
export async function startTogether(opts: {
  onStep?: (step: StartTogetherStep) => void;
} = {}): Promise<StartTogetherResult> {
  const step = (s: StartTogetherStep) => {
    addBreadcrumb(`start-together:${s}`);
    opts.onStep?.(s);
  };
  const inApp = isInAppBrowser();
  track("start_together_clicked", { in_app_browser: inApp });

  // 1. Sign in --------------------------------------------------------
  step("sign_in");
  let user = getCurrentUser() ?? (await waitForAuthReady());
  if (!user) {
    try {
      setPending(true);
      user = await signInWithGoogle("start_together");
    } catch (err) {
      if (err instanceof AuthError && err.kind === "redirecting") {
        throw new StartTogetherRedirecting();
      }
      setPending(false);
      track("start_together_failed", { step: "sign_in" });
      throw new StartTogetherError("sign_in", asMessage(err, "Sign-in didn't finish."), err);
    }
  }
  setPending(false);

  // 2. Save -----------------------------------------------------------
  step("save");
  const design = useDesign.getState().design;
  const roomName = design.room.name?.trim() || "Our room";
  let savedId = useDesign.getState().activeSavedRoomId;
  const alreadyShared = useDesign.getState().activeSharedRoomId;
  if (!savedId && !alreadyShared) {
    try {
      savedId = useLibrary.getState().saveDesign(design, roomName);
      useDesign.setState({ activeSavedRoomId: savedId });
    } catch (err) {
      track("start_together_failed", { step: "save" });
      captureException(err, { context: "startTogether.save" });
      throw new StartTogetherError("save", "Couldn't save the room.", err);
    }
  }
  const roomId = alreadyShared ?? savedId;
  if (!roomId) {
    track("start_together_failed", { step: "save" });
    throw new StartTogetherError("save", "Couldn't save the room.");
  }

  // 3. Promote --------------------------------------------------------
  step("promote");
  if (!alreadyShared) {
    try {
      const existing = await fetchSharedRoom(roomId);
      if (!existing) {
        await callCreateSharedRoom({ roomId, name: roomName, design });
      }
    } catch (err) {
      track("start_together_failed", { step: "promote" });
      throw new StartTogetherError(
        "promote",
        asMessage(err, "Couldn't turn this into a shared room."),
        err
      );
    }
  }

  // 4. Invite ---------------------------------------------------------
  step("invite");
  let link: string;
  try {
    const res = await callCreateShareInvite({ kind: "link", roomId });
    link = buildInviteLink(res.inviteId, { from: user.displayName });
  } catch (err) {
    track("start_together_failed", { step: "invite" });
    throw new StartTogetherError(
      "invite",
      asMessage(err, "Couldn't create an invite link."),
      err
    );
  }

  // 5. Open the live session -------------------------------------------
  step("open");
  if (!alreadyShared) {
    const result = await openRoom(roomId, { forceShared: true });
    if (result !== "shared") {
      track("start_together_failed", { step: "open" });
      throw new StartTogetherError(
        "open",
        "The room is shared and your link is ready, but live sync didn't start. Reload to join it."
      );
    }
  }

  recordInviteLink(link, roomId);
  return { link, roomId, roomName };
}

function asMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
